'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type LoanStatus = 'al-dia' | 'proximo-vencer' | 'vencido' | 'moroso' | 'cancelado';
export type ClientStatus = 'activo' | 'inactivo' | 'moroso';
export type FollowupType = 'whatsapp' | 'llamada' | 'visita' | 'cobro' | 'promesa-pago' | 'pago-recibido';
export type TaskStatus = 'pendiente' | 'en-progreso' | 'completada' | 'cancelada' | 'reprogramada';
export type TaskPriority = 'baja' | 'media' | 'alta' | 'critica';
export type BookStatus = 'pendiente' | 'leyendo' | 'terminado' | 'abandonado';
export type HabitFrequency = 'diario' | 'semanal' | 'mensual';
export type BusinessIdeaStatus = 'idea' | 'investigacion' | 'evaluacion' | 'preparacion' | 'activo' | 'descartado';
export type EgaStudioStatus = 'prospecto' | 'contactado' | 'cotizacion' | 'negociacion' | 'confirmado' | 'produccion' | 'entregado' | 'cobrado';
export type MusicStatus = 'idea' | 'composicion' | 'produccion' | 'mezcla' | 'masterizacion' | 'portada' | 'lista' | 'publicada' | 'promocion';
export type TransactionType = 'ingreso' | 'gasto';
export type Theme = 'dark' | 'light' | 'auto';

// ─── Productive Activity Types ─────────────────────────────────────────────────

export type ProductiveActivityStatus = 'pendiente' | 'en-progreso' | 'completada' | 'parcial' | 'no-realizada';
export type ActivityFrequency = 'diario' | 'lunes-viernes' | 'lunes' | 'martes' | 'miercoles' | 'jueves' | 'viernes' | 'sabado' | 'domingo' | 'semanal' | 'mensual' | 'personalizado';
export type ActivityCategory = 'Finanzas' | 'Negocios' | 'PrestaMass' | 'EGA Studio' | 'EGA Studio Music' | 'Salud' | 'Educacion' | 'Personal' | 'Otra';

export interface ProductiveActivity {
  id: string;
  name: string;
  description: string;
  category: ActivityCategory;
  priority: 'alta' | 'media' | 'baja';
  generatesIncome: boolean;
  dailyGoal: string;
  measurableUnit: string;
  goalTarget: number;
  scheduledTime: string;
  estimatedMinutes: number;
  frequency: ActivityFrequency;
  specificDays: string[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DailyProductiveRecord {
  id: string;
  activityId: string;
  activityName: string;
  category: ActivityCategory;
  priority: 'alta' | 'media' | 'baja';
  date: string; // YYYY-MM-DD
  status: ProductiveActivityStatus;
  completed: boolean;
  goalTarget: number;
  measurableUnit: string;
  result: number;
  completionPercent: number;
  scheduledTime: string;
  startTime: string;
  endTime: string;
  minutesDedicated: number;
  generatesIncome: boolean;
  incomeGenerated: number;
  expectedIncome: number;
  observations: string;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  whatsapp?: string;
  company?: string;
  address?: string;
  website?: string;
  facebook?: string;
  instagram?: string;
  theme: Theme;
  currency: string;
  logoUrl?: string;
}

export interface Client {
  id: string;
  code: string;
  name: string;
  phone: string;
  whatsapp?: string;
  email?: string;
  address: string;
  idNumber: string;
  birthDate?: string;
  registeredAt: string;
  status: ClientStatus;
  totalLoans: number;
  activeLoans: number;
  totalDebt: number;
  observations: string;
}

export interface Loan {
  id: string;
  code: string;
  clientId: string;
  clientName: string;
  capital: number;
  loanDate: string;
  interestRate: number;
  interestType: string;
  frequency: string;
  quotaCount: number;
  interest: number;
  capitalPaid: number;
  capitalPending: number;
  quota: number;
  mora: number;
  multa: number;
  paymentDate: string;
  nextPayment: string;
  startPaymentDate: string;
  paymentDay: number;
  amortizationMethod: string;
  status: LoanStatus;
  observations: string;
}

export interface LoanPayment {
  id: string;
  loanId: string;
  loanCode: string;
  clientName: string;
  date: string;
  capital: number;
  interest: number;
  mora: number;
  multa: number;
  total: number;
  observations: string;
}

export interface Followup {
  id: string;
  clientId: string;
  clientName: string;
  loanId?: string;
  loanCode: string;
  date: string;
  type: FollowupType;
  status: string;
  result: string;
  nextFollowup: string;
  nextAction: string;
  observations: string;
  amount?: number;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  duration: number;
  category: string;
  business: string;
  client: string;
  phone: string;
  location: string;
  priority: TaskPriority;
  economicImpact: string;
  potentialIncome: number;
  status: TaskStatus;
  reminder: boolean;
  reminderTime?: string;
  recurring: boolean;
  recurringType?: string;
  notes: string;
  createdAt: string;
}

export interface Transaction {
  id: string;
  type: TransactionType;
  date: string;
  concept: string;
  amount: number;
  category: string;
  business: string;
  account: string;
  paymentMethod: string;
  description: string;
  observations: string;
  createdAt: string;
}

export interface Account {
  id: string;
  name: string;
  type: string;
  initialBalance: number;
  balance: number;
  currency: string;
  description: string;
  status: string;
  createdAt: string;
}

export interface Book {
  id: string;
  title: string;
  author: string;
  isbn?: string;
  category: string;
  editorial?: string;
  publisher?: string;
  status: BookStatus;
  startDate: string;
  endDate: string;
  purchaseDate: string;
  price: number;
  stock: number;
  format: string;
  rating: number;
  summary: string;
  notes: string;
  lessons: string;
  coverUrl?: string;
  createdAt: string;
}

export interface Habit {
  id: string;
  name: string;
  description: string;
  category: string;
  frequency: HabitFrequency;
  days: string[];
  time: string;
  goal: number;
  startDate: string;
  status: string;
  streak: number;
  bestStreak: number;
  completedToday: boolean;
  totalCompleted: number;
  completionRate: number;
  createdAt: string;
  logs: { date: string; completed: boolean }[];
}

export interface HealthRecord {
  id: string;
  date: string;
  weight?: number;
  height?: number;
  physicalActivity?: string;
  activityDuration?: number;
  diet?: string;
  waist?: number;
  hip?: number;
  chest?: number;
  goal?: string;
  type: string;
  time: string;
  description: string;
  doctor: string;
  clinic: string;
  notes: string;
  reminder: boolean;
  reminderDate: string;
  createdAt: string;
}

export interface EgaClient {
  id: string;
  name: string;
  idNumber?: string;
  phone: string;
  whatsapp?: string;
  email: string;
  address?: string;
  facebook?: string;
  instagram?: string;
  tiktok?: string;
  clientType?: string;
  service: string;
  contactDate: string;
  eventDate: string;
  price: number;
  advance: number;
  balance: number;
  status: EgaStudioStatus;
  nextFollowup: string;
  observations: string;
  createdAt: string;
}

export interface Song {
  id: string;
  title: string;
  artist: string;
  genre: string;
  album?: string;
  releaseType?: string;
  createdDate: string;
  launchDate: string;
  composer?: string;
  producer?: string;
  duration?: string;
  status: MusicStatus;
  coverUrl: string;
  lyrics: string;
  audioUrl: string;
  platforms: string[];
  observations: string;
  createdAt: string;
}

export interface Product {
  id: string;
  name: string;
  code?: string;
  category?: string;
  description?: string;
  cost: number;
  normalPrice: number;
  prePrice: number;
  resellerPrice: number;
  stock: number;
  unit: string;
  provider?: string;
  imageUrl?: string;
  active: boolean;
  notes?: string;
  createdAt: string;
}

export interface Order {
  id: string;
  code: string;
  clientName: string;
  clientPhone: string;
  items: { productId: string; productName: string; quantity: number; price: number; cost: number }[];
  total: number;
  totalCost: number;
  profit: number;
  saleType: string;
  paymentMethod: string;
  status: string;
  date: string;
  observations: string;
  createdAt: string;
}

export interface Business {
  id: string;
  name: string;
  logoUrl?: string;
  description: string;
  category: string;
  phone?: string;
  whatsapp?: string;
  email?: string;
  address?: string;
  website?: string;
  facebook?: string;
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  startDate: string;
  income: number;
  expenses: number;
  profit: number;
  clients: number;
  status: string;
  goals: string;
  notes?: string;
  createdAt: string;
}

export interface BusinessIdea {
  id: string;
  name: string;
  description: string;
  category?: string;
  problemSolved?: string;
  targetAudience?: string;
  investment: number;
  estimatedIncome: number;
  estimatedProfit: number;
  risk: string;
  timeRequired: string;
  demand: string;
  status: BusinessIdeaStatus;
  priority?: string;
  date?: string;
  score: number;
  observations: string;
  createdAt: string;
}

// ─── Store State ──────────────────────────────────────────────────────────────

interface AppState {
  user: User | null;
  isAuthenticated: boolean;
  clients: Client[];
  loans: Loan[];
  loanPayments: LoanPayment[];
  followups: Followup[];
  tasks: Task[];
  transactions: Transaction[];
  accounts: Account[];
  books: Book[];
  habits: Habit[];
  healthRecords: HealthRecord[];
  egaClients: EgaClient[];
  songs: Song[];
  products: Product[];
  orders: Order[];
  businesses: Business[];
  businessIdeas: BusinessIdea[];
  productiveActivities: ProductiveActivity[];
  dailyProductiveRecords: DailyProductiveRecord[];
}

interface AppActions {
  // Auth
  login: (email: string, password: string) => Promise<boolean>;
  signup: (name: string, email: string, password: string, phone?: string) => Promise<boolean>;
  logout: () => void;
  updateUser: (data: Partial<User>) => void;

  // Supabase hydration
  hydrateFromSupabase: (data: Partial<AppState> & { user: User }) => void;
  setTasks: (tasks: Task[]) => void;
  setClients: (clients: Client[]) => void;
  setLoans: (loans: Loan[]) => void;
  setTransactions: (transactions: Transaction[]) => void;

  // Clients
  addClient: (data: Omit<Client, 'id' | 'code' | 'registeredAt' | 'totalLoans' | 'activeLoans' | 'totalDebt'>) => Client;
  updateClient: (id: string, data: Partial<Client>) => void;
  deleteClient: (id: string) => void;

  // Loans
  addLoan: (data: Omit<Loan, 'id' | 'code' | 'capitalPaid' | 'mora' | 'multa'>) => Loan;
  updateLoan: (id: string, data: Partial<Loan>) => void;
  deleteLoan: (id: string) => void;
  registerPayment: (payment: Omit<LoanPayment, 'id'>) => void;

  // Followups
  addFollowup: (data: Omit<Followup, 'id'>) => void;
  updateFollowup: (id: string, data: Partial<Followup>) => void;
  deleteFollowup: (id: string) => void;

  // Tasks
  addTask: (data: Omit<Task, 'id' | 'createdAt'>) => Task;
  updateTask: (id: string, data: Partial<Task>) => void;
  deleteTask: (id: string) => void;
  toggleTask: (id: string) => void;

  // Transactions
  addTransaction: (data: Omit<Transaction, 'id' | 'createdAt'>) => void;
  updateTransaction: (id: string, data: Partial<Transaction>) => void;
  deleteTransaction: (id: string) => void;

  // Accounts
  addAccount: (data: Omit<Account, 'id' | 'createdAt'>) => void;
  updateAccount: (id: string, data: Partial<Account>) => void;
  deleteAccount: (id: string) => void;

  // Books
  addBook: (data: Omit<Book, 'id' | 'createdAt'>) => void;
  updateBook: (id: string, data: Partial<Book>) => void;
  deleteBook: (id: string) => void;

  // Habits
  addHabit: (data: Omit<Habit, 'id' | 'createdAt' | 'streak' | 'bestStreak' | 'completedToday' | 'totalCompleted' | 'completionRate' | 'logs'>) => void;
  updateHabit: (id: string, data: Partial<Habit>) => void;
  deleteHabit: (id: string) => void;
  toggleHabit: (id: string) => void;

  // Health
  addHealthRecord: (data: Omit<HealthRecord, 'id' | 'createdAt'>) => void;
  updateHealthRecord: (id: string, data: Partial<HealthRecord>) => void;
  deleteHealthRecord: (id: string) => void;

  // EGA Studio
  addEgaClient: (data: Omit<EgaClient, 'id' | 'createdAt'>) => void;
  updateEgaClient: (id: string, data: Partial<EgaClient>) => void;
  deleteEgaClient: (id: string) => void;

  // Songs
  addSong: (data: Omit<Song, 'id' | 'createdAt'>) => void;
  updateSong: (id: string, data: Partial<Song>) => void;
  deleteSong: (id: string) => void;

  // Products
  addProduct: (data: Omit<Product, 'id' | 'createdAt'>) => void;
  updateProduct: (id: string, data: Partial<Product>) => void;
  deleteProduct: (id: string) => void;

  // Orders
  addOrder: (data: Omit<Order, 'id' | 'code' | 'createdAt'>) => void;
  updateOrder: (id: string, data: Partial<Order>) => void;
  deleteOrder: (id: string) => void;

  // Businesses
  addBusiness: (data: Omit<Business, 'id' | 'createdAt'>) => void;
  updateBusiness: (id: string, data: Partial<Business>) => void;
  deleteBusiness: (id: string) => void;

  // Business Ideas
  addBusinessIdea: (data: Omit<BusinessIdea, 'id' | 'createdAt'>) => void;
  updateBusinessIdea: (id: string, data: Partial<BusinessIdea>) => void;
  deleteBusinessIdea: (id: string) => void;

  // Productive Activities
  addProductiveActivity: (data: Omit<ProductiveActivity, 'id' | 'createdAt' | 'updatedAt'>) => ProductiveActivity;
  updateProductiveActivity: (id: string, data: Partial<ProductiveActivity>) => void;
  deleteProductiveActivity: (id: string) => void;

  // Daily Productive Records
  addDailyRecord: (data: Omit<DailyProductiveRecord, 'id' | 'createdAt' | 'updatedAt'>) => DailyProductiveRecord;
  updateDailyRecord: (id: string, data: Partial<DailyProductiveRecord>) => void;
  deleteDailyRecord: (id: string) => void;
  toggleDailyRecord: (id: string) => void;
  generateDailyRecords: (date: string) => void;

  // Module history clearing
  clearModule: (module: keyof Omit<AppState, 'user' | 'isAuthenticated'>) => void;
  clearAllData: () => void;
}

type AppStore = AppState & AppActions;

// ─── Context ──────────────────────────────────────────────────────────────────

const AppContext = createContext<AppStore | null>(null);

// Single storage key per user — fixes the persistence bug where save used one key and load used another
function getUserStorageKey(userId: string) {
  return `ega_data_${userId}`;
}

function getInitialState(): AppState {
  return {
    user: null,
    isAuthenticated: false,
    clients: [],
    loans: [],
    loanPayments: [],
    followups: [],
    tasks: [],
    transactions: [],
    accounts: [],
    books: [],
    habits: [],
    healthRecords: [],
    egaClients: [],
    songs: [],
    products: [],
    orders: [],
    businesses: [],
    businessIdeas: [],
    productiveActivities: [],
    dailyProductiveRecords: [],
  };
}

function genId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function nowIso() {
  return new Date().toISOString();
}

function todayStr() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AppState>(getInitialState);

  // Only persist theme preference to localStorage (non-critical UI preference)
  // All critical data is stored in Supabase
  useEffect(() => {
    if (typeof window === 'undefined' || !state.user?.id) return;
    const themeKey = `ega_theme_${state.user.id}`;
    if (state.user.theme) localStorage.setItem(themeKey, state.user.theme);
  }, [state.user?.theme, state.user?.id]);

  // Apply theme to document whenever user theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !state.user) return;
    applyTheme(state.user.theme || 'dark');
  }, [state.user?.theme]);

  const set = useCallback((updater: (prev: AppState) => AppState) => {
    setState(updater);
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────────

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    await new Promise((r) => setTimeout(r, 800));
    const usersRaw = typeof window !== 'undefined' ? localStorage.getItem('ega_users') : null;
    const users: { id: string; name: string; email: string; phone?: string; password: string }[] = usersRaw ? JSON.parse(usersRaw) : [];
    const found = users.find((u) => u.email.toLowerCase() === email.toLowerCase() && u.password === password);
    if (!found) return false;

    // Load user-specific data from the correct key
    const userDataRaw = typeof window !== 'undefined' ? localStorage.getItem(getUserStorageKey(found.id)) : null;
    const userData: Partial<AppState> = userDataRaw ? JSON.parse(userDataRaw) : {};

    set(() => ({
      ...getInitialState(),
      clients: userData.clients || [],
      loans: userData.loans || [],
      loanPayments: userData.loanPayments || [],
      followups: userData.followups || [],
      tasks: userData.tasks || [],
      transactions: userData.transactions || [],
      accounts: userData.accounts || [],
      books: userData.books || [],
      habits: userData.habits || [],
      healthRecords: userData.healthRecords || [],
      egaClients: userData.egaClients || [],
      songs: userData.songs || [],
      products: userData.products || [],
      orders: userData.orders || [],
      businesses: userData.businesses || [],
      businessIdeas: userData.businessIdeas || [],
      productiveActivities: userData.productiveActivities || [],
      dailyProductiveRecords: userData.dailyProductiveRecords || [],
      user: {
        id: found.id,
        name: found.name,
        email: found.email,
        phone: found.phone,
        theme: (userData as AppState).user?.theme || 'dark',
        currency: 'L',
        logoUrl: (userData as AppState).user?.logoUrl,
        whatsapp: (userData as AppState).user?.whatsapp,
        company: (userData as AppState).user?.company,
        address: (userData as AppState).user?.address,
        website: (userData as AppState).user?.website,
        facebook: (userData as AppState).user?.facebook,
        instagram: (userData as AppState).user?.instagram,
      },
      isAuthenticated: true,
    }));
    return true;
  }, [set]);

  const signup = useCallback(async (name: string, email: string, password: string, phone?: string): Promise<boolean> => {
    await new Promise((r) => setTimeout(r, 1000));
    const usersRaw = typeof window !== 'undefined' ? localStorage.getItem('ega_users') : null;
    const users: { id: string; name: string; email: string; phone?: string; password: string }[] = usersRaw ? JSON.parse(usersRaw) : [];
    if (users.find((u) => u.email.toLowerCase() === email.toLowerCase())) return false;
    const newUser = { id: genId(), name, email, phone, password };
    users.push(newUser);
    if (typeof window !== 'undefined') localStorage.setItem('ega_users', JSON.stringify(users));
    set(() => ({
      ...getInitialState(),
      user: { id: newUser.id, name, email, phone, theme: 'dark', currency: 'L' },
      isAuthenticated: true,
    }));
    return true;
  }, [set]);

  const logout = useCallback(() => {
    set(() => getInitialState());
  }, [set]);

  const updateUser = useCallback((data: Partial<User>) => {
    set((prev) => ({ ...prev, user: prev.user ? { ...prev.user, ...data } : prev.user }));
  }, [set]);

  // ── Supabase Hydration ────────────────────────────────────────────────────

  const hydrateFromSupabase = useCallback((data: Partial<AppState> & { user: User }) => {
    set(() => ({
      ...getInitialState(),
      isAuthenticated: true,
      ...data,
    }));
  }, [set]);

  const setTasks = useCallback((tasks: Task[]) => {
    set((prev) => ({ ...prev, tasks }));
  }, [set]);

  const setClients = useCallback((clients: Client[]) => {
    set((prev) => ({ ...prev, clients }));
  }, [set]);

  const setLoans = useCallback((loans: Loan[]) => {
    set((prev) => ({ ...prev, loans }));
  }, [set]);

  const setTransactions = useCallback((transactions: Transaction[]) => {
    set((prev) => ({ ...prev, transactions }));
  }, [set]);

  // ── Clients ───────────────────────────────────────────────────────────────

  const addClient = useCallback((data: Omit<Client, 'id' | 'code' | 'registeredAt' | 'totalLoans' | 'activeLoans' | 'totalDebt'>): Client => {
    let newClient!: Client;
    set((prev) => {
      const code = `CLI-${String(prev.clients.length + 1).padStart(6, '0')}`;
      newClient = { ...data, id: genId(), code, registeredAt: todayStr(), totalLoans: 0, activeLoans: 0, totalDebt: 0 };
      return { ...prev, clients: [...prev.clients, newClient] };
    });
    return newClient;
  }, [set]);

  const updateClient = useCallback((id: string, data: Partial<Client>) => {
    set((prev) => ({ ...prev, clients: prev.clients.map((c) => c.id === id ? { ...c, ...data } : c) }));
  }, [set]);

  const deleteClient = useCallback((id: string) => {
    set((prev) => ({ ...prev, clients: prev.clients.filter((c) => c.id !== id) }));
  }, [set]);

  // ── Loans ─────────────────────────────────────────────────────────────────

  const addLoan = useCallback((data: Omit<Loan, 'id' | 'code' | 'capitalPaid' | 'mora' | 'multa'>): Loan => {
    let newLoan!: Loan;
    set((prev) => {
      const code = `PRE-${String(prev.loans.length + 1).padStart(6, '0')}`;
      newLoan = { ...data, id: genId(), code, capitalPaid: 0, mora: 0, multa: 0 };
      const clients = prev.clients.map((c) => c.id === data.clientId
        ? { ...c, totalLoans: c.totalLoans + 1, activeLoans: c.activeLoans + 1, totalDebt: c.totalDebt + data.capital }
        : c
      );
      return { ...prev, loans: [...prev.loans, newLoan], clients };
    });
    return newLoan;
  }, [set]);

  const updateLoan = useCallback((id: string, data: Partial<Loan>) => {
    set((prev) => ({ ...prev, loans: prev.loans.map((l) => l.id === id ? { ...l, ...data } : l) }));
  }, [set]);

  const deleteLoan = useCallback((id: string) => {
    set((prev) => {
      const loan = prev.loans.find((l) => l.id === id);
      const clients = loan ? prev.clients.map((c) => c.id === loan.clientId
        ? { ...c, activeLoans: Math.max(0, c.activeLoans - 1), totalDebt: Math.max(0, c.totalDebt - loan.capitalPending) }
        : c
      ) : prev.clients;
      return { ...prev, loans: prev.loans.filter((l) => l.id !== id), clients };
    });
  }, [set]);

  const registerPayment = useCallback((payment: Omit<LoanPayment, 'id'>) => {
    set((prev) => {
      const newPayment: LoanPayment = { ...payment, id: genId() };
      const loans = prev.loans.map((l) => {
        if (l.id !== payment.loanId) return l;
        const newCapitalPaid = l.capitalPaid + payment.capital;
        const newCapitalPending = Math.max(0, l.capitalPending - payment.capital);
        const newStatus: LoanStatus = newCapitalPending <= 0 ? 'cancelado' : l.status;
        return { ...l, capitalPaid: newCapitalPaid, capitalPending: newCapitalPending, mora: Math.max(0, l.mora - payment.mora), multa: Math.max(0, l.multa - payment.multa), status: newStatus };
      });
      const loan = prev.loans.find((l) => l.id === payment.loanId);
      const clients = loan ? prev.clients.map((c) => c.id === loan.clientId
        ? { ...c, totalDebt: Math.max(0, c.totalDebt - payment.capital) }
        : c
      ) : prev.clients;
      return { ...prev, loans, clients, loanPayments: [...prev.loanPayments, newPayment] };
    });
  }, [set]);

  // ── Followups ─────────────────────────────────────────────────────────────

  const addFollowup = useCallback((data: Omit<Followup, 'id'>) => {
    set((prev) => ({ ...prev, followups: [...prev.followups, { ...data, id: genId() }] }));
  }, [set]);

  const updateFollowup = useCallback((id: string, data: Partial<Followup>) => {
    set((prev) => ({ ...prev, followups: prev.followups.map((f) => f.id === id ? { ...f, ...data } : f) }));
  }, [set]);

  const deleteFollowup = useCallback((id: string) => {
    set((prev) => ({ ...prev, followups: prev.followups.filter((f) => f.id !== id) }));
  }, [set]);

  // ── Tasks ─────────────────────────────────────────────────────────────────

  const addTask = useCallback((data: Omit<Task, 'id' | 'createdAt'>): Task => {
    let newTask!: Task;
    set((prev) => {
      newTask = { ...data, id: genId(), createdAt: nowIso() };
      return { ...prev, tasks: [...prev.tasks, newTask] };
    });
    return newTask;
  }, [set]);

  const updateTask = useCallback((id: string, data: Partial<Task>) => {
    set((prev) => ({ ...prev, tasks: prev.tasks.map((t) => t.id === id ? { ...t, ...data } : t) }));
  }, [set]);

  const deleteTask = useCallback((id: string) => {
    set((prev) => ({ ...prev, tasks: prev.tasks.filter((t) => t.id !== id) }));
  }, [set]);

  // True toggle: pendiente ↔ completada
  const toggleTask = useCallback((id: string) => {
    set((prev) => ({
      ...prev,
      tasks: prev.tasks.map((t) =>
        t.id === id
          ? { ...t, status: t.status === 'completada' ? 'pendiente' : 'completada' }
          : t
      ),
    }));
  }, [set]);

  // ── Transactions ──────────────────────────────────────────────────────────

  const addTransaction = useCallback((data: Omit<Transaction, 'id' | 'createdAt'>) => {
    set((prev) => ({ ...prev, transactions: [...prev.transactions, { ...data, id: genId(), createdAt: nowIso() }] }));
  }, [set]);

  const updateTransaction = useCallback((id: string, data: Partial<Transaction>) => {
    set((prev) => ({ ...prev, transactions: prev.transactions.map((t) => t.id === id ? { ...t, ...data } : t) }));
  }, [set]);

  const deleteTransaction = useCallback((id: string) => {
    set((prev) => ({ ...prev, transactions: prev.transactions.filter((t) => t.id !== id) }));
  }, [set]);

  // ── Accounts ──────────────────────────────────────────────────────────────

  const addAccount = useCallback((data: Omit<Account, 'id' | 'createdAt'>) => {
    set((prev) => ({ ...prev, accounts: [...prev.accounts, { ...data, id: genId(), createdAt: nowIso() }] }));
  }, [set]);

  const updateAccount = useCallback((id: string, data: Partial<Account>) => {
    set((prev) => ({ ...prev, accounts: prev.accounts.map((a) => a.id === id ? { ...a, ...data } : a) }));
  }, [set]);

  const deleteAccount = useCallback((id: string) => {
    set((prev) => ({ ...prev, accounts: prev.accounts.filter((a) => a.id !== id) }));
  }, [set]);

  // ── Books ─────────────────────────────────────────────────────────────────

  const addBook = useCallback((data: Omit<Book, 'id' | 'createdAt'>) => {
    set((prev) => ({ ...prev, books: [...prev.books, { ...data, id: genId(), createdAt: nowIso() }] }));
  }, [set]);

  const updateBook = useCallback((id: string, data: Partial<Book>) => {
    set((prev) => ({ ...prev, books: prev.books.map((b) => b.id === id ? { ...b, ...data } : b) }));
  }, [set]);

  const deleteBook = useCallback((id: string) => {
    set((prev) => ({ ...prev, books: prev.books.filter((b) => b.id !== id) }));
  }, [set]);

  // ── Habits ────────────────────────────────────────────────────────────────

  const addHabit = useCallback((data: Omit<Habit, 'id' | 'createdAt' | 'streak' | 'bestStreak' | 'completedToday' | 'totalCompleted' | 'completionRate' | 'logs'>) => {
    set((prev) => ({ ...prev, habits: [...prev.habits, { ...data, id: genId(), createdAt: nowIso(), streak: 0, bestStreak: 0, completedToday: false, totalCompleted: 0, completionRate: 0, logs: [] }] }));
  }, [set]);

  const updateHabit = useCallback((id: string, data: Partial<Habit>) => {
    set((prev) => ({ ...prev, habits: prev.habits.map((h) => h.id === id ? { ...h, ...data } : h) }));
  }, [set]);

  const deleteHabit = useCallback((id: string) => {
    set((prev) => ({ ...prev, habits: prev.habits.filter((h) => h.id !== id) }));
  }, [set]);

  const toggleHabit = useCallback((id: string) => {
    const today = todayStr();
    set((prev) => ({
      ...prev,
      habits: prev.habits.map((h) => {
        if (h.id !== id) return h;
        const wasCompleted = h.completedToday;
        const newLogs = wasCompleted
          ? h.logs.filter((l) => l.date !== today)
          : [...h.logs, { date: today, completed: true }];
        const newTotal = wasCompleted ? Math.max(0, h.totalCompleted - 1) : h.totalCompleted + 1;
        const newStreak = wasCompleted ? Math.max(0, h.streak - 1) : h.streak + 1;
        const newBestStreak = Math.max(h.bestStreak, newStreak);
        const daysActive = Math.max(1, Math.ceil((Date.now() - new Date(h.createdAt).getTime()) / 86400000));
        const completionRate = Math.round((newTotal / daysActive) * 100);
        return { ...h, completedToday: !wasCompleted, totalCompleted: newTotal, streak: newStreak, bestStreak: newBestStreak, completionRate, logs: newLogs };
      }),
    }));
  }, [set]);

  // ── Health ────────────────────────────────────────────────────────────────

  const addHealthRecord = useCallback((data: Omit<HealthRecord, 'id' | 'createdAt'>) => {
    set((prev) => ({ ...prev, healthRecords: [...prev.healthRecords, { ...data, id: genId(), createdAt: nowIso() }] }));
  }, [set]);

  const updateHealthRecord = useCallback((id: string, data: Partial<HealthRecord>) => {
    set((prev) => ({ ...prev, healthRecords: prev.healthRecords.map((h) => h.id === id ? { ...h, ...data } : h) }));
  }, [set]);

  const deleteHealthRecord = useCallback((id: string) => {
    set((prev) => ({ ...prev, healthRecords: prev.healthRecords.filter((h) => h.id !== id) }));
  }, [set]);

  // ── EGA Studio ────────────────────────────────────────────────────────────

  const addEgaClient = useCallback((data: Omit<EgaClient, 'id' | 'createdAt'>) => {
    set((prev) => ({ ...prev, egaClients: [...prev.egaClients, { ...data, id: genId(), createdAt: nowIso() }] }));
  }, [set]);

  const updateEgaClient = useCallback((id: string, data: Partial<EgaClient>) => {
    set((prev) => ({ ...prev, egaClients: prev.egaClients.map((c) => c.id === id ? { ...c, ...data } : c) }));
  }, [set]);

  const deleteEgaClient = useCallback((id: string) => {
    set((prev) => ({ ...prev, egaClients: prev.egaClients.filter((c) => c.id !== id) }));
  }, [set]);

  // ── Songs ─────────────────────────────────────────────────────────────────

  const addSong = useCallback((data: Omit<Song, 'id' | 'createdAt'>) => {
    set((prev) => ({ ...prev, songs: [...prev.songs, { ...data, id: genId(), createdAt: nowIso() }] }));
  }, [set]);

  const updateSong = useCallback((id: string, data: Partial<Song>) => {
    set((prev) => ({ ...prev, songs: prev.songs.map((s) => s.id === id ? { ...s, ...data } : s) }));
  }, [set]);

  const deleteSong = useCallback((id: string) => {
    set((prev) => ({ ...prev, songs: prev.songs.filter((s) => s.id !== id) }));
  }, [set]);

  // ── Products ──────────────────────────────────────────────────────────────

  const addProduct = useCallback((data: Omit<Product, 'id' | 'createdAt'>) => {
    set((prev) => ({ ...prev, products: [...prev.products, { ...data, id: genId(), createdAt: nowIso() }] }));
  }, [set]);

  const updateProduct = useCallback((id: string, data: Partial<Product>) => {
    set((prev) => ({ ...prev, products: prev.products.map((p) => p.id === id ? { ...p, ...data } : p) }));
  }, [set]);

  const deleteProduct = useCallback((id: string) => {
    set((prev) => ({ ...prev, products: prev.products.filter((p) => p.id !== id) }));
  }, [set]);

  // ── Orders ────────────────────────────────────────────────────────────────

  const addOrder = useCallback((data: Omit<Order, 'id' | 'code' | 'createdAt'>) => {
    set((prev) => {
      const code = `PED-${String(prev.orders.length + 1).padStart(6, '0')}`;
      return { ...prev, orders: [...prev.orders, { ...data, id: genId(), code, createdAt: nowIso() }] };
    });
  }, [set]);

  const updateOrder = useCallback((id: string, data: Partial<Order>) => {
    set((prev) => ({ ...prev, orders: prev.orders.map((o) => o.id === id ? { ...o, ...data } : o) }));
  }, [set]);

  const deleteOrder = useCallback((id: string) => {
    set((prev) => ({ ...prev, orders: prev.orders.filter((o) => o.id !== id) }));
  }, [set]);

  // ── Businesses ────────────────────────────────────────────────────────────

  const addBusiness = useCallback((data: Omit<Business, 'id' | 'createdAt'>) => {
    set((prev) => ({ ...prev, businesses: [...prev.businesses, { ...data, id: genId(), createdAt: nowIso() }] }));
  }, [set]);

  const updateBusiness = useCallback((id: string, data: Partial<Business>) => {
    set((prev) => ({ ...prev, businesses: prev.businesses.map((b) => b.id === id ? { ...b, ...data } : b) }));
  }, [set]);

  const deleteBusiness = useCallback((id: string) => {
    set((prev) => ({ ...prev, businesses: prev.businesses.filter((b) => b.id !== id) }));
  }, [set]);

  // ── Business Ideas ────────────────────────────────────────────────────────

  const addBusinessIdea = useCallback((data: Omit<BusinessIdea, 'id' | 'createdAt'>) => {
    set((prev) => ({ ...prev, businessIdeas: [...prev.businessIdeas, { ...data, id: genId(), createdAt: nowIso() }] }));
  }, [set]);

  const updateBusinessIdea = useCallback((id: string, data: Partial<BusinessIdea>) => {
    set((prev) => ({ ...prev, businessIdeas: prev.businessIdeas.map((b) => b.id === id ? { ...b, ...data } : b) }));
  }, [set]);

  const deleteBusinessIdea = useCallback((id: string) => {
    set((prev) => ({ ...prev, businessIdeas: prev.businessIdeas.filter((b) => b.id !== id) }));
  }, [set]);

  // ── Productive Activities ─────────────────────────────────────────────────

  const addProductiveActivity = useCallback((data: Omit<ProductiveActivity, 'id' | 'createdAt' | 'updatedAt'>): ProductiveActivity => {
    let newActivity!: ProductiveActivity;
    set((prev) => {
      newActivity = { ...data, id: genId(), createdAt: nowIso(), updatedAt: nowIso() };
      return { ...prev, productiveActivities: [...prev.productiveActivities, newActivity] };
    });
    return newActivity;
  }, [set]);

  const updateProductiveActivity = useCallback((id: string, data: Partial<ProductiveActivity>) => {
    set((prev) => ({
      ...prev,
      productiveActivities: prev.productiveActivities.map((a) =>
        a.id === id ? { ...a, ...data, updatedAt: nowIso() } : a
      ),
    }));
  }, [set]);

  const deleteProductiveActivity = useCallback((id: string) => {
    set((prev) => ({
      ...prev,
      productiveActivities: prev.productiveActivities.filter((a) => a.id !== id),
    }));
  }, [set]);

  // ── Daily Productive Records ───────────────────────────────────────────────

  const addDailyRecord = useCallback((data: Omit<DailyProductiveRecord, 'id' | 'createdAt' | 'updatedAt'>): DailyProductiveRecord => {
    let newRecord!: DailyProductiveRecord;
    set((prev) => {
      newRecord = { ...data, id: genId(), createdAt: nowIso(), updatedAt: nowIso() };
      return { ...prev, dailyProductiveRecords: [...prev.dailyProductiveRecords, newRecord] };
    });
    return newRecord;
  }, [set]);

  const updateDailyRecord = useCallback((id: string, data: Partial<DailyProductiveRecord>) => {
    set((prev) => ({
      ...prev,
      dailyProductiveRecords: prev.dailyProductiveRecords.map((r) =>
        r.id === id ? { ...r, ...data, updatedAt: nowIso() } : r
      ),
    }));
  }, [set]);

  const deleteDailyRecord = useCallback((id: string) => {
    set((prev) => ({
      ...prev,
      dailyProductiveRecords: prev.dailyProductiveRecords.filter((r) => r.id !== id),
    }));
  }, [set]);

  // Toggle completed status of a daily record (true toggle)
  const toggleDailyRecord = useCallback((id: string) => {
    set((prev) => ({
      ...prev,
      dailyProductiveRecords: prev.dailyProductiveRecords.map((r) => {
        if (r.id !== id) return r;
        const nowCompleted = !r.completed;
        return {
          ...r,
          completed: nowCompleted,
          status: nowCompleted ? 'completada' : 'pendiente',
          completionPercent: nowCompleted ? (r.completionPercent > 0 ? r.completionPercent : 100) : 0,
          updatedAt: nowIso(),
        };
      }),
    }));
  }, [set]);

  // Generate daily records for a given date from active productive activities
  // Skips activities that already have a record for that date (no duplicates)
  const generateDailyRecords = useCallback((date: string) => {
    set((prev) => {
      const dateObj = new Date(date + 'T00:00:00');
      const dayOfWeek = dateObj.getDay(); // 0=Sun,1=Mon,...,6=Sat
      const dayNames = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
      const dayName = dayNames[dayOfWeek];
      const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;

      const newRecords: DailyProductiveRecord[] = [];

      for (const activity of prev.productiveActivities) {
        if (!activity.active) continue;

        // Check if record already exists for this activity+date
        const exists = prev.dailyProductiveRecords.some(
          (r) => r.activityId === activity.id && r.date === date
        );
        if (exists) continue;

        // Check frequency match
        let shouldCreate = false;
        switch (activity.frequency) {
          case 'diario': shouldCreate = true; break;
          case 'lunes-viernes': shouldCreate = isWeekday; break;
          case 'semanal': shouldCreate = dayOfWeek === 1; break; // Mondays
          case 'mensual': shouldCreate = dateObj.getDate() === 1; break;
          case 'lunes': shouldCreate = dayOfWeek === 1; break;
          case 'martes': shouldCreate = dayOfWeek === 2; break;
          case 'miercoles': shouldCreate = dayOfWeek === 3; break;
          case 'jueves': shouldCreate = dayOfWeek === 4; break;
          case 'viernes': shouldCreate = dayOfWeek === 5; break;
          case 'sabado': shouldCreate = dayOfWeek === 6; break;
          case 'domingo': shouldCreate = dayOfWeek === 0; break;
          case 'personalizado':
            shouldCreate = activity.specificDays.includes(dayName);
            break;
          default: shouldCreate = true;
        }

        if (shouldCreate) {
          newRecords.push({
            id: genId(),
            activityId: activity.id,
            activityName: activity.name,
            category: activity.category,
            priority: activity.priority,
            date,
            status: 'pendiente',
            completed: false,
            goalTarget: activity.goalTarget,
            measurableUnit: activity.measurableUnit,
            result: 0,
            completionPercent: 0,
            scheduledTime: activity.scheduledTime,
            startTime: '',
            endTime: '',
            minutesDedicated: 0,
            generatesIncome: activity.generatesIncome,
            incomeGenerated: 0,
            expectedIncome: 0,
            observations: '',
            createdAt: nowIso(),
            updatedAt: nowIso(),
          });
        }
      }

      if (newRecords.length === 0) return prev;
      return { ...prev, dailyProductiveRecords: [...prev.dailyProductiveRecords, ...newRecords] };
    });
  }, [set]);

  // ── Module clearing ───────────────────────────────────────────────────────

  const clearModule = useCallback((module: keyof Omit<AppState, 'user' | 'isAuthenticated'>) => {
    set((prev) => ({ ...prev, [module]: [] }));
  }, [set]);

  const clearAllData = useCallback(() => {
    set((prev) => ({
      ...getInitialState(),
      user: prev.user,
      isAuthenticated: prev.isAuthenticated,
    }));
  }, [set]);

  const store: AppStore = {
    ...state,
    login, signup, logout, updateUser,
    hydrateFromSupabase, setTasks, setClients, setLoans, setTransactions,
    addClient, updateClient, deleteClient,
    addLoan, updateLoan, deleteLoan, registerPayment,
    addFollowup, updateFollowup, deleteFollowup,
    addTask, updateTask, deleteTask, toggleTask,
    addTransaction, updateTransaction, deleteTransaction,
    addAccount, updateAccount, deleteAccount,
    addBook, updateBook, deleteBook,
    addHabit, updateHabit, deleteHabit, toggleHabit,
    addHealthRecord, updateHealthRecord, deleteHealthRecord,
    addEgaClient, updateEgaClient, deleteEgaClient,
    addSong, updateSong, deleteSong,
    addProduct, updateProduct, deleteProduct,
    addOrder, updateOrder, deleteOrder,
    addBusiness, updateBusiness, deleteBusiness,
    addBusinessIdea, updateBusinessIdea, deleteBusinessIdea,
    addProductiveActivity, updateProductiveActivity, deleteProductiveActivity,
    addDailyRecord, updateDailyRecord, deleteDailyRecord, toggleDailyRecord, generateDailyRecords,
    clearModule, clearAllData,
  };

  return React.createElement(AppContext.Provider, { value: store }, children);
}

export function useStore() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useStore must be used within AppProvider');
  return ctx;
}

// ─── Theme Helper ─────────────────────────────────────────────────────────────

export function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'light') {
    root.classList.remove('dark');
    root.classList.add('light');
  } else if (theme === 'dark') {
    root.classList.remove('light');
    root.classList.add('dark');
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
    root.classList.toggle('light', !prefersDark);
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function fmtCurrency(amount: number): string {
  return `L ${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

export function fmtDate(isoOrDDMMYYYY: string): string {
  if (!isoOrDDMMYYYY) return '';
  if (isoOrDDMMYYYY.includes('/')) return isoOrDDMMYYYY;
  const d = new Date(isoOrDDMMYYYY);
  if (isNaN(d.getTime())) return isoOrDDMMYYYY;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}
